import type { Collector } from '../collectors/types';
import type { BrowserInfo, Priority, Tip } from '../content/tips';
import { ESTIMATE_METHOD_VERSION, ESTIMATES, GROUPS, IN_GROUP_DECAY, MAX_TOTAL_BITS, SOURCES, type GroupId } from '../entropy/estimates';
import { BAND_THRESHOLDS, whatIfScore, type Band } from '../entropy/score';
import type { ProgressStatus, RunResult } from '../run';
import { clear, copyText, flash, h, joinList } from './dom';
import { buildReport, downloadJson, reportToText, type ExportReport } from './export';
import { renderCategory } from './section';
import { renderShareControls } from './share-card';
import { BAND_TITLE, renderSummary } from './summary';

// ── Progress ─────────────────────────────────────────────────────────────────────────────────────

const PROGRESS_TEXT: Record<ProgressStatus | 'waiting', string> = {
  waiting: 'waiting',
  running: 'collecting…',
  ok: 'done',
  timeout: 'timed out',
  error: 'failed',
};

export function renderProgressList(list: HTMLElement, collectors: readonly Collector[]): Map<string, HTMLElement> {
  clear(list);
  const items = new Map<string, HTMLElement>();
  for (const c of collectors) {
    const state = h('span', { class: 'progress-state' }, PROGRESS_TEXT.waiting);
    const li = h('li', { 'data-state': 'waiting' }, h('span', { class: 'progress-name' }, c.title), state);
    items.set(c.id, li);
    list.append(li);
  }
  return items;
}

export function updateProgress(item: HTMLElement | undefined, status: ProgressStatus | 'waiting', ms?: number) {
  if (!item) return;
  item.dataset.state = status;
  const state = item.querySelector('.progress-state');
  if (state) state.textContent = `${PROGRESS_TEXT[status]}${ms !== undefined && status !== 'running' ? ` · ${ms} ms` : ''}`;
}

// ── Results ──────────────────────────────────────────────────────────────────────────────────────

const PRIORITY_TEXT: Record<Priority, string> = { high: 'High impact', medium: 'Medium impact', low: 'Good practice' };

function renderTip(t: Tip): HTMLElement {
  return h(
    'li',
    { class: 'tip', 'data-priority': t.priority },
    h('p', { class: 'tip-priority' }, PRIORITY_TEXT[t.priority]),
    h('h3', null, t.title),
    h('p', null, t.body),
    t.steps?.length ? h('ol', { class: 'tip-steps' }, t.steps.map((s) => h('li', null, s))) : null,
  );
}

export interface ResultTargets {
  summary: HTMLElement;
  categories: HTMLElement;
  tips: HTMLElement;
  tipsIntro: HTMLElement;
  exportActions: HTMLElement;
  share: HTMLElement;
  whatIfBar: HTMLElement;
}

// ── What if ──────────────────────────────────────────────────────────────────────────────────────

const signed = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : '±'}${Math.abs(n).toFixed(1)}`;

/**
 * Wires the per-signal "What if this were hidden?" toggles to a sticky bar that shows the
 * recomputed estimate. The real result (summary, hash, export) never changes.
 */
function whatIf(result: RunResult, bar: HTMLElement, categories: HTMLElement) {
  const hidden = new Set<string>();
  const text = h('p', { class: 'whatif-text', role: 'status' });
  const reset = h('button', { type: 'button', class: 'button small' }, 'Reset');
  clear(bar);
  bar.append(h('div', { class: 'whatif-inner' }, text, reset));
  bar.hidden = true;

  const update = () => {
    if (!hidden.size) {
      bar.hidden = true;
      text.textContent = '';
      return;
    }
    const before = result.score;
    const after = whatIfScore(result.signals, hidden);
    const bandChange = (b: Band) => (b === before.band ? BAND_TITLE[b] : `${BAND_TITLE[b]} (was ${BAND_TITLE[before.band].toLowerCase()})`);
    let why = '';
    if (Math.abs(after.totalBits - before.totalBits) < 0.05) {
      if (after.uncappedBits >= MAX_TOTAL_BITS) {
        why = ` No change: the rest of your fingerprint is still above the ${MAX_TOTAL_BITS}-bit ceiling.`;
      } else {
        const groups = [...new Set([...hidden].map((id) => ESTIMATES[id]?.group).filter((g): g is GroupId => !!g))];
        const saturated = groups.filter((g) => after.groups.find((x) => x.id === g)?.combined === GROUPS[g].cap);
        if (saturated.length)
          why = ` No change: the other signals in ${joinList(saturated.map((g) => `“${GROUPS[g].label}”`))} still reach that group's cap, because they repeat much of the same information.`;
      }
    }
    const main = h(
      'span',
      { class: 'whatif-main' },
      `What if ${hidden.size} signal${hidden.size === 1 ? ' were' : 's were'} hidden: ${before.totalBits.toFixed(1)} → ${after.totalBits.toFixed(1)} bits (${signed(after.totalBits - before.totalBits)}) · ${bandChange(after.band)}.`,
    );
    text.replaceChildren(main, ...(why ? [' ', h('span', { class: 'whatif-why' }, why.trim())] : []));
    bar.hidden = false;
  };

  reset.addEventListener('click', () => {
    for (const input of categories.querySelectorAll<HTMLInputElement>('.whatif-toggle input:checked')) {
      input.checked = false;
      input.closest('.signal')?.removeAttribute('data-hidden');
    }
    hidden.clear();
    update();
  });

  return (id: string, isHidden: boolean) => {
    if (isHidden) hidden.add(id);
    else hidden.delete(id);
    update();
  };
}

export function renderResults(result: RunResult, browser: BrowserInfo, tips: Tip[], targets: ResultTargets, onAbout: () => void): ExportReport {
  clear(targets.summary);
  targets.summary.append(renderSummary(result, browser, onAbout));

  clear(targets.categories);
  const expandAll = h('button', { type: 'button', class: 'link-button' }, 'Expand all');
  const collapseAll = h('button', { type: 'button', class: 'link-button' }, 'Collapse all');
  const onWhatIf = whatIf(result, targets.whatIfBar, targets.categories);
  const sections = result.categories.map((c) => renderCategory(c, onWhatIf));
  expandAll.addEventListener('click', () => sections.forEach((d) => (d.open = true)));
  collapseAll.addEventListener('click', () => sections.forEach((d) => (d.open = false)));
  targets.categories.append(h('p', { class: 'category-tools' }, expandAll, ' · ', collapseAll), ...sections);

  targets.tipsIntro.textContent = `Ordered by impact for ${browser.name}${browser.mobile ? ' on mobile' : ''}, based on what this check found.`;
  clear(targets.tips);
  targets.tips.append(...tips.map(renderTip));

  clear(targets.exportActions);
  const report = buildReport(result, browser);
  const status = h('span', { class: 'export-status', role: 'status' });
  const copyBtn = h('button', { type: 'button', class: 'button' }, 'Copy report as text');
  copyBtn.addEventListener('click', async () => flash(copyBtn, (await copyText(reportToText(report))) ? 'Copied' : 'Copy failed', status));
  const jsonBtn = h('button', { type: 'button', class: 'button' }, 'Download JSON');
  jsonBtn.addEventListener('click', () => {
    downloadJson(report);
    status.textContent = 'Saved to your downloads.';
  });
  targets.exportActions.append(copyBtn, jsonBtn, status);

  clear(targets.share);
  targets.share.append(renderShareControls(result));
  return report;
}

// ── About the score ──────────────────────────────────────────────────────────────────────────────

export function renderAbout(container: HTMLElement) {
  clear(container);
  const rows = (Object.keys(GROUPS) as GroupId[]).map((id) => {
    const g = GROUPS[id];
    return h('tr', null, h('th', { scope: 'row' }, g.label), h('td', null, String(g.cap)), h('td', null, `×${g.weight}`), h('td', null, g.why));
  });
  container.append(
    h('h3', null, `Method v${ESTIMATE_METHOD_VERSION}`),
    h(
      'ol',
      null,
      h('li', null, 'Each signal gets an estimated number of identifying bits: a published average for that attribute, adjusted for your value where possible. Randomized values count 0, blocked ones a fraction of a bit, rounded or standardized values a little.'),
      h('li', null, `Related signals are grouped. Within a group the strongest signal counts fully and every other one counts ${IN_GROUP_DECAY * 100}%, because they mostly repeat the same fact (a user agent and a platform string both say “Windows”). Each group has a ceiling.`),
      h('li', null, 'Groups are added up with weights. Groups that are largely predictable from your browser and OS (fonts, graphics, audio, media) count less.'),
      h('li', null, `The total is capped at ${MAX_TOTAL_BITS} bits, roughly enough to single out one browser among everyone online.`),
      h('li', null, `Bands: below ${BAND_THRESHOLDS.moderate} bits is Low, ${BAND_THRESHOLDS.moderate}–${BAND_THRESHOLDS.high} is Moderate, and ${BAND_THRESHOLDS.high} or more is High.`),
    ),
    h(
      'p',
      null,
      'Why it is only an estimate: a real tracker compares you against millions of other visitors. This page has no population to compare against and never sends anything anywhere, so it cannot know how rare your exact values are. It only knows how varied each attribute tends to be. Large studies also find real browsers less unique than summed per-attribute figures suggest, so read the number as a rough indicator, not a measurement.',
    ),
    h(
      'div',
      { class: 'table-wrap' },
      h(
        'table',
        null,
        h('caption', null, 'Correlation groups'),
        h('thead', null, h('tr', null, h('th', { scope: 'col' }, 'Group'), h('th', { scope: 'col' }, 'Cap (bits)'), h('th', { scope: 'col' }, 'Weight'), h('th', { scope: 'col' }, 'Why'))),
        h('tbody', null, rows),
      ),
    ),
    h('h3', null, 'Sources'),
    h(
      'ul',
      { class: 'sources' },
      Object.values(SOURCES).map((s) => h('li', null, s.citation, s.url ? [' ', h('a', { href: s.url, rel: 'noopener noreferrer', target: '_blank' }, s.url)] : null)),
    ),
  );
}
